<template>
  <page>
    <lazy-div :load="doInit">
      <v-card>
        <v-toolbar v-if="reviewMode" dense color="grey lighten-3" flat>
          <v-icon>mdi-pencil</v-icon>
          <v-toolbar-title>Edit Bank Account </v-toolbar-title>
        </v-toolbar>
        <v-toolbar v-if="queryMode" dense color="grey lighten-3" flat>
          <v-icon>mdi-magnify</v-icon>
          <v-toolbar-title>Query Bank Account </v-toolbar-title>
        </v-toolbar>
        <v-toolbar v-if="newBank" dense color="grey lighten-3" flat>
          <v-icon>mdi-plus</v-icon>
          <v-toolbar-title>Create Bank Account</v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <message :messages="messages"></message>
          <v-container grid-list-md>
            <v-layout row>
              <v-flex xs6 v-if="newBank">
                <v-text-field
                  :label="labels.bankNo"
                  v-model="bankForm.bankNo"
                  :error-messages="errors.collect('bankNo')"
                  data-vv-name="bankNo"
                  :data-vv-as="labels.bankNo"
                  v-validate="'required|max:2'"
                ></v-text-field>
              </v-flex>
              <v-flex xs6 v-else>
                <v-subheader class="queryLabel">{{ labels.bankNo }}</v-subheader>
                <div class="queryValue">{{ bankForm.bankNo }}</div>
              </v-flex>
              <v-flex xs6>
                <v-text-field
                  :readonly="queryMode"
                  :label="labels.bankAcctName"
                  v-model="bankForm.bankAcctName"
                  :error-messages="errors.collect('bankAcctName')"
                  data-vv-name="bankAcctName"
                  :data-vv-as="labels.bankAcctName"
                  v-validate="'required|max:120'"
                ></v-text-field>
              </v-flex>
            </v-layout>
            <v-layout row>
              <v-flex xs6>
                <v-autocomplete
                  :readonly="queryMode"
                  :label="labels.bankAbbrev"
                  :items="bankAbbrevList"
                  item-text="label"
                  item-value="value"
                  v-model="bankForm.bankAbbrev"
                  :error-messages="errors.collect('bankAbbrev')"
                  data-vv-name="bankAbbrev"
                  :data-vv-as="labels.bankAbbrev"
                  v-validate="'required'"
                ></v-autocomplete>
              </v-flex>
              <v-flex xs6>
                <v-text-field
                  :readonly="queryMode"
                  :label="labels.fullBankNo"
                  v-model="bankForm.fullBankNo"
                  :error-messages="errors.collect('fullBankNo')"
                  data-vv-name="fullBankNo"
                  :data-vv-as="labels.fullBankNo"
                  v-validate="'required|max:20'"
                ></v-text-field>
              </v-flex>
            </v-layout>
            <v-layout row>
              <v-flex xs6>
                <v-autocomplete
                  :readonly="queryMode"
                  :label="labels.bankAcctCurr"
                  :items="currList"
                  item-text="label"
                  item-value="value"
                  v-model="bankForm.bankAcctCurr"
                  :error-messages="errors.collect('bankAcctCurr')"
                  data-vv-name="bankAcctCurr"
                  :data-vv-as="labels.bankAcctCurr"
                  v-validate="'required'"
                ></v-autocomplete>
              </v-flex>
              <v-flex xs6>
                <v-text-field
                  :readonly="queryMode"
                  v-num-mask="{ negative: true, intScale: 18, decScale: 2 }"
                  :label="labels.bankAcctBal"
                  v-model="bankForm.bankAcctBal"
                  :error-messages="errors.collect('bankAcctBal')"
                  data-vv-name="bankAcctBal"
                  :data-vv-as="labels.bankAcctBal"
                  v-validate="'required'"
                  :disabled="!newBank"
                ></v-text-field>
              </v-flex>
            </v-layout>
            <div class="body-2">
              {{ labels.postGlacct }} &nbsp; <v-icon v-if="!queryMode" @click="doGlacctNoHelplist('postGlacct')">mdi-magnify</v-icon> &nbsp;
            </div>
            <v-layout row>
              <v-flex xs3>
                <v-text-field
                  :label="labels.branch"
                  v-model="bankForm.branch"
                  :error-messages="errors.collect('branch')"
                  data-vv-name="branch"
                  :data-vv-as="labels.branch"
                  v-validate="'required|max:4'"
                  readonly
                ></v-text-field>
              </v-flex>
              <v-flex xs3>
                <v-text-field
                  :label="labels.glacctNo"
                  v-model="bankForm.glacctNo"
                  :error-messages="errors.collect('glacctNo')"
                  data-vv-name="glacctNo"
                  :data-vv-as="labels.glacctNo"
                  v-validate="'required|max:16'"
                  readonly
                ></v-text-field>
              </v-flex>
              <v-flex xs3>
                <v-text-field
                  :label="labels.fund"
                  v-model="bankForm.fund"
                  :error-messages="errors.collect('fund')"
                  data-vv-name="fund"
                  :data-vv-as="labels.fund"
                  v-validate="'max:4'"
                  readonly
                ></v-text-field>
              </v-flex>
              <v-flex xs3>
                <v-text-field
                  :label="labels.acctNoSfx"
                  v-model="bankForm.acctNoSfx"
                  :error-messages="errors.collect('acctNoSfx')"
                  data-vv-name="acctNoSfx"
                  :data-vv-as="labels.acctNoSfx"
                  v-validate="'max:4'"
                  readonly
                ></v-text-field>
              </v-flex>
            </v-layout>
            <div class="body-2">
              {{ labels.postBankChrgGlacct }} &nbsp;
              <v-icon v-if="!queryMode" @click="doGlacctNoHelplist('postBankChrgGlacct')">mdi-magnify</v-icon> &nbsp;
              <v-icon v-if="!queryMode" @click="doClearGlacctNo('postBankChrgGlacct')" title="Clear Bank Charge GL Account">mdi-close</v-icon>
            </div>
            <v-layout row>
              <v-flex xs3>
                <v-text-field
                  :label="labels.bankChrgBrn"
                  v-model="bankForm.bankChrgBrn"
                  :error-messages="errors.collect('bankChrgBrn')"
                  data-vv-name="bankChrgBrn"
                  :data-vv-as="labels.bankChrgBrn"
                  v-validate="'max:4'"
                  readonly
                ></v-text-field>
              </v-flex>
              <v-flex xs3>
                <v-text-field
                  :label="labels.bankChrgGlacctNo"
                  v-model="bankForm.bankChrgGlacctNo"
                  :error-messages="errors.collect('bankChrgGlacctNo')"
                  data-vv-name="bankChrgGlacctNo"
                  :data-vv-as="labels.bankChrgGlacctNo"
                  v-validate="'max:16'"
                  readonly
                ></v-text-field>
              </v-flex>
              <v-flex xs3>
                <v-text-field
                  :label="labels.bankChrgFund"
                  v-model="bankForm.bankChrgFund"
                  :error-messages="errors.collect('bankChrgFund')"
                  data-vv-name="bankChrgFund"
                  :data-vv-as="labels.bankChrgFund"
                  v-validate="'max:4'"
                  readonly
                ></v-text-field>
              </v-flex>
              <v-flex xs3>
                <v-text-field
                  :label="labels.bankChrgAcctNoSfx"
                  v-model="bankForm.bankChrgAcctNoSfx"
                  :error-messages="errors.collect('bankChrgAcctNoSfx')"
                  data-vv-name="bankChrgAcctNoSfx"
                  :data-vv-as="labels.bankChrgAcctNoSfx"
                  v-validate="'max:4'"
                  readonly
                ></v-text-field>
              </v-flex>
            </v-layout>
            <v-layout row>
              <v-flex xs6>
                <v-autocomplete
                  :readonly="queryMode"
                  :label="labels.defPaytIntType"
                  :items="paytMethList"
                  item-text="label"
                  item-value="value"
                  v-model="bankForm.defPaytIntType"
                  :error-messages="errors.collect('defPaytIntType')"
                  data-vv-name="defPaytIntType"
                  :data-vv-as="labels.defPaytIntType"
                  v-validate="'max:4'"
                ></v-autocomplete>
              </v-flex>
              <v-flex xs6>
                <v-checkbox
                  :readonly="queryMode"
                  :label="labels.payBankCurr"
                  v-model="bankForm.payBankCurr"
                  :error-messages="errors.collect('payBankCurr')"
                  data-vv-name="payBankCurr"
                  :data-vv-as="labels.payBankCurr"
                ></v-checkbox>
              </v-flex>
            </v-layout>
            <v-layout row>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ labels.createdBy }}</v-subheader>
                <div class="queryValue">{{ bankForm.createdBy }}</div>
              </v-flex>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ labels.createdDate }}</v-subheader>
                <div class="queryValue">{{ bankForm.createdDate | formatDate }}</div>
              </v-flex>
            </v-layout>
            <v-layout row>
              <v-flex xs6 v-if="!newBank">
                <v-subheader class="queryLabel">{{ labels.modifiedBy }}</v-subheader>
                <div class="queryValue">{{ bankForm.modifiedBy }}</div>
              </v-flex>
              <v-flex xs6 v-if="!newBank">
                <v-subheader class="queryLabel">{{ labels.modifiedDate }}</v-subheader>
                <div class="queryValue">{{ bankForm.modifiedDate | formatDate }}</div>
              </v-flex>
              <v-flex xs12>
                <v-spacer class="mt-3"></v-spacer>
                <v-btn class="mr-2" color="error" @click="doCancel" :loading="submitting">
                  Cancel
                  <v-icon right dark>mdi-cancel</v-icon>
                </v-btn>
                <v-btn color="primary" v-if="!queryMode" dark @click="doSave" :loading="submitting">
                  Save Bank Account
                  <v-icon right>mdi-content-save</v-icon>
                </v-btn>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-dialog v-model="glacctNoHelplist" width="1000px" persistent>
          <gl-acct-no-helplist
            :apiURI="glacctnoAPI"
            :primaryKey="glacctNoHelplistPK"
            @selectGlAcctNo="doSelectGlAcctNo($event)"
            @close="glacctNoHelplist = false"
          ></gl-acct-no-helplist>
        </v-dialog>
      </v-card>
    </lazy-div>
  </page>
</template>

<script>
import MessageMixin from '@/mixins/message'
import { expandURI } from '@/utils/ajax'
import { extractMenu } from '@/utils/extract'
import { formatDisplayDate } from '@/utils/date'
import GlAcctNoHelplist from '../../helplist/GLAcctNoHelplist'
import { removeFormatNumber, formatDisplayNumber } from '@/utils/number.js'
export default {
  data() {
    return {
      labels: {
        bankNo: 'Bank No.',
        bankAcctName: 'Bank Account Name',
        bankAbbrev: 'Abbreviated Name',
        fullBankNo: 'Full Bank No.',
        bankAcctCurr: 'Bank Account Currency',
        bankAcctBal: 'Bank Account Balance',
        postGlacct: 'Post GL Account No.',
        branch: 'Branch',
        glacctNo: 'GL Account No.',
        fund: 'Fund',
        acctNoSfx: 'Account No. Suffix',
        postBankChrgGlacct: 'Post Bank Charge GL Account No.',
        bankChrgBrn: 'Branch',
        bankChrgGlacctNo: 'GL Account No.',
        bankChrgFund: 'Fund',
        bankChrgAcctNoSfx: 'Account No. Suffix',
        defPaytIntType: 'Default Payment Method',
        payBankCurr: 'Pay only Bank Currency',
        createdBy: 'Created By',
        createdDate: 'Created Date',
        modifiedBy: 'Modified By',
        modifiedDate: 'Modified Date'
      },
      bankForm: {
        bankNo: null,
        bankAcctName: null,
        bankAbbrev: null,
        fullBankNo: null,
        bankAcctCurr: null,
        bankAcctBal: null,
        branch: null,
        glacctNo: null,
        fund: null,
        acctNoSfx: null,
        bankChrgBrn: null,
        bankChrgGlacctNo: null,
        bankChrgFund: null,
        bankChrgAcctNoSfx: null,
        defPaytIntType: null,
        payBankCurr: false,
        createdBy: null,
        createdDate: null,
        modifiedBy: null,
        modifiedDate: null
      },
      bankAbbrevList: null,
      paytMethList: null,
      currList: null,
      glacctnoAPI: null,
      glacctNoHelplistPK: null,
      glacctNoHelplist: false,
      submitting: false,
      queryMode: false,
      reviewMode: false
    }
  },
  methods: {
    async loadHelplist() {
      const apiURI = this.menu.bankHelplist
      if (apiURI) {
        try {
          const result = await this.$http.get(apiURI)
          this.glacctnoAPI = extractMenu(result.data._links).glacctNoHelplist
          const { data } = result.data
          if (data) {
            this.paytMethList = data.paytMeth
            this.bankAbbrevList = data.bankAbbrev
            this.currList = data.curr
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
    },
    async doInit() {
      await this.loadHelplist()
      if (!this.newBank) {
        const apiURI = this.menu.bank
        if (apiURI) {
          try {
            const expand = {
              bankNo: this.bankNo
            }
            const result = await this.$http.get(expandURI(apiURI, expand))
            const { data } = result.data
            if (data) {
              this.bankForm.bankNo = data.bank.bankNo
              this.bankForm.bankAcctName = data.bank.bankAcctName
              this.bankForm.bankAbbrev = data.bank.bankAbbrev
              this.bankForm.fullBankNo = data.bank.fullBankNo
              this.bankForm.bankAcctCurr = data.bank.bankAcctCurr
              this.bankForm.bankAcctBal = formatDisplayNumber(data.bank.bankAcctBal)
              this.bankForm.branch = data.bank.branch
              this.bankForm.glacctNo = data.bank.glacctNo
              this.bankForm.fund = data.bank.fund
              this.bankForm.acctNoSfx = data.bank.acctNoSfx
              this.bankForm.bankChrgBrn = data.bank.bankChrgBrn
              this.bankForm.bankChrgGlacctNo = data.bank.bankChrgGlacctNo
              this.bankForm.bankChrgFund = data.bank.bankChrgFund
              this.bankForm.bankChrgAcctNoSfx = data.bank.bankChrgAcctNoSfx
              this.bankForm.defPaytIntType = data.bank.defPaytIntType
              this.bankForm.payBankCurr = data.bank.payBankCurr
              this.bankForm.createdBy = data.createdBy
              this.bankForm.createdDate = data.createdDate
              this.bankForm.modifiedBy = data.modifiedBy
              this.bankForm.modifiedDate = data.modifiedDate
            }
          } catch (err) {
            const { data } = err.response
            if (data) {
              this.message(data)
              this.fieldMessage(data)
            }
          }
        }
      } else {
        this.bankForm.createdBy = this.$store.state.token.firstName + ' ' + this.$store.state.token.lastName
        this.bankForm.createdDate = new Date()
      }
    },
    doGlacctNoHelplist(PK) {
      this.resetMessages()
      if (PK) {
        this.glacctNoHelplistPK = PK
        this.glacctNoHelplist = true
      }
    },
    doSelectGlAcctNo(data) {
      if (data && data.primaryKey && data.data) {
        switch (data.primaryKey) {
          case 'postGlacct': {
            this.bankForm.branch = data.data.branch
            this.bankForm.glacctNo = data.data.glacctNo
            this.bankForm.fund = data.data.fund
            this.bankForm.acctNoSfx = data.data.acctNoSfx
            break
          }
          case 'postBankChrgGlacct': {
            this.bankForm.bankChrgBrn = data.data.branch
            this.bankForm.bankChrgGlacctNo = data.data.glacctNo
            this.bankForm.bankChrgFund = data.data.fund
            this.bankForm.bankChrgAcctNoSfx = data.data.acctNoSfx
            break
          }
        }
      }
      this.glacctNoHelplist = false
    },
    doClearGlacctNo(PK) {
      if (PK) {
        switch (PK) {
          // case 'postGlacct': {
          //   this.bankForm.branch = null
          //   this.bankForm.glacctNo = null
          //   this.bankForm.fund = null
          //   this.bankForm.acctNoSfx = null
          //   break
          // }
          case 'postBankChrgGlacct': {
            this.bankForm.bankChrgBrn = null
            this.bankForm.bankChrgGlacctNo = null
            this.bankForm.bankChrgFund = null
            this.bankForm.bankChrgAcctNoSfx = null
            break
          }
        }
      }
    },
    formatNumber() {
      this.bankForm.bankAcctBal = removeFormatNumber(this.bankForm.bankAcctBal)
    },
    async doSave() {
      this.resetMessages()
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.submitting = true
          this.formatNumber()
          const config = {
            method: this.newBank ? 'post' : 'put',
            url: this.newBank ? this.menu.createBank : this.menu.updateBank,
            data: this.bankForm
          }
          if (config.url) {
            this.$http
              .request(config)
              .then((result) => {
                const { data } = result
                this.notify(data)
                this.doCancel()
                this.submitting = false
              })
              .catch((err) => {
                this.submitting = false
                const { data } = err.response
                if (data) {
                  this.message(data)
                  this.fieldMessage(data)
                }
              })
          }
        } else {
          this.promptFieldCheck()
        }
      })
    },
    doCancel() {
      this.$router.push({ name: 'bankAcctList' })
    }
  },
  computed: {
    newBank() {
      return this.bankNo === 'new'
    },
    menu() {
      return this.$store.state.bankParam.menu
    },
    bankAcctMode() {
      return this.$route.params.bankAcctMode
    }
  },
  created() {
    if (this.bankAcctMode === 'Query') {
      this.queryMode = true
    } else if (this.bankAcctMode === 'Review') {
      this.reviewMode = true
    }
  },
  filters: {
    formatDate(value) {
      if (value) {
        return formatDisplayDate(value)
      } else {
        return null
      }
    }
  },
  components: {
    GlAcctNoHelplist
  },
  props: ['bankNo'],
  mixins: [MessageMixin]
}
</script>

<style>
</style>
